import React, { useState, useMemo } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  Input,
  VStack,
  Text,
  useToast,
  Spinner,
  InputGroup,
  InputRightElement,
  IconButton,
  Textarea,
  Checkbox,
  Skeleton,
} from "@chakra-ui/react";
import { FaCopy, FaPlay } from "react-icons/fa";
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";

const TRAINING_LEVEL_OPTIONS = [
  { value: "EMT", label: "EMT" },
  { value: "AEMT", label: "AEMT" },
  { value: "Paramedic", label: "Paramedic" },
  { value: "CriticalCare", label: "Critical Care (CCP/FP-C)" },
];

const normalizeTrainingLevel = (value) => {
  const source = String(value || "").trim().toLowerCase();
  if (source.includes("critical") || source.includes("ccp") || source.includes("fp-c") || source.includes("fpc")) return "CriticalCare";
  if (source.includes("paramedic")) return "Paramedic";
  if (source.includes("aemt") || source.includes("advanced emt") || source.includes("emt-advanced")) return "AEMT";
  if (source.includes("emt")) return "EMT";
  return "EMT";
};

const RunScenarioModal = ({ isOpen, onClose, scenario, instructorCohorts }) => {
  const [selectedCohort, setSelectedCohort] = useState("");
  const [selectedEvaluationForm, setSelectedEvaluationForm] = useState("");
  const [expirationHours, setExpirationHours] = useState("72");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [generatedLink, setGeneratedLink] = useState("");
  const [evaluationNotes, setEvaluationNotes] = useState("");
  const [allowEmailToStudent, setAllowEmailToStudent] = useState(false);
  const [trainingLevel, setTrainingLevel] = useState(normalizeTrainingLevel(scenario?.levelOfCare));

  const toast = useToast();
  const user = useUser(api);
  const [{ data, error, fetching }, generateScenarioRunLink] = useGlobalAction(api.generateScenarioRunLink);
  const [{ data: templatesData, fetching: templatesFetching }, getTemplates] = useGlobalAction(
    api.getAvailableEvaluationTemplates
  );

  // Fetch available evaluation templates when user is loaded
  React.useEffect(() => {
    if (user?.id) {
      getTemplates({ userId: user.id, trainingLevel });
    }
  }, [user?.id, trainingLevel, getTemplates]);

  // Build form type options from template data
  const availableFormTypes = useMemo(() => {
    if (!templatesData?.templates) return [];
    
    return templatesData.templates.map(template => ({
      value: template.key,
      label: template.displayName,
      description: template.description,
      trainingLevels: template.requiredTrainingLevel
    }));
  }, [templatesData]);

  // Watch for successful link generation
  React.useEffect(() => {
    if (data && data.link) {
      setGeneratedLink(data.link);
      setSuccessMessage("Scenario link generated successfully!");
      toast({
        title: "Success",
        description: "Scenario link has been generated",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [data, toast]);

  // Watch for errors
  React.useEffect(() => {
    if (error) {
      setErrorMessage(error.message || "Failed to generate link");
      toast({
        title: "Error",
        description: error.message || "Failed to generate scenario link",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  React.useEffect(() => {
    console.log('=== RUN SCENARIO MODAL DEBUG ===');
    console.log('instructorCohorts:', instructorCohorts);
    console.log('instructorCohorts length:', instructorCohorts?.length);
    console.log('============================');
  }, [instructorCohorts]);

  const handleGenerateLink = async () => {
    if (!selectedCohort || !selectedEvaluationForm) {
      setErrorMessage("Please select both a cohort and an evaluation form");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await generateScenarioRunLink({
        scenarioId: scenario.id,
        cohortId: selectedCohort,
        evaluationForm: selectedEvaluationForm,
        evaluationNotes: evaluationNotes || null,
        expirationHours: parseInt(expirationHours, 10),
        allowEmailToStudent: allowEmailToStudent,
        trainingLevel,
      });
      // Don't check result here - use useEffect to watch for data changes
    } catch (err) {
      console.error('Error calling action:', err);
      setErrorMessage(err.message || "Failed to generate scenario link. Please try again.");
      toast({
        title: "Error",
        description: "Failed to generate scenario link",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    toast({
      title: "Copied",
      description: "Link copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleClose = () => {
    setSelectedCohort("");
    setSelectedEvaluationForm("");
    setExpirationHours("72");
    setGeneratedLink("");
    setSuccessMessage("");
    setErrorMessage("");
    setEvaluationNotes("");
    setAllowEmailToStudent(false);
    setTrainingLevel(normalizeTrainingLevel(scenario?.levelOfCare));
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Run Scenario: {scenario?.title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Section 1: Cohort Selection */}
            <FormControl isRequired>
              <FormLabel>Select Cohort</FormLabel>
              <Select
                placeholder="Choose a cohort"
                value={selectedCohort}
                onChange={(e) => setSelectedCohort(e.target.value)}
                isDisabled={isSubmitting || !!generatedLink}
              >
                <option value="__NO_COHORT__">No Cohort (Manual Entry)</option>
                {instructorCohorts?.map((cohort) => (
                  <option key={cohort.id} value={cohort.id}>
                    {cohort.displayName || cohort.name}
                  </option>
                ))}
              </Select>
              <Text fontSize="sm" color="gray.600" mt={1}>
                Choose a cohort, or select "No Cohort" for demos/troubleshooting where you'll manually enter student info
              </Text>
            </FormControl>

            {/* Section 2: Evaluation Form Selection */}
            <FormControl isRequired>
              <FormLabel>Clinical Training Scope</FormLabel>
              <Select
                value={trainingLevel}
                onChange={(e) => setTrainingLevel(e.target.value)}
                isDisabled={isSubmitting || !!generatedLink}
              >
                {TRAINING_LEVEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <Text fontSize="sm" color="gray.600" mt={1}>
                Determines which scoped interventions/critical actions are emphasized in this run.
              </Text>
            </FormControl>

            {/* Section 3: Evaluation Form Selection */}
            <FormControl isRequired>
              <FormLabel>Evaluation Form</FormLabel>
              {templatesFetching ? (
                <Skeleton height="40px" />
              ) : availableFormTypes.length === 0 ? (
                <VStack align="stretch" spacing={2}>
                  <Select isDisabled placeholder="No templates available" />
                  <Text fontSize="sm" color="red.500">
                    No evaluation templates available. Contact your institution administrator.
                  </Text>
                </VStack>
              ) : (
                <>
                  <Select
                    placeholder="Choose an evaluation form"
                    value={selectedEvaluationForm}
                    onChange={(e) => setSelectedEvaluationForm(e.target.value)}
                    isDisabled={isSubmitting || !!generatedLink}
                  >
                    {availableFormTypes.map((formType) => (
                      <option key={formType.value} value={formType.value}>
                        {formType.label}
                      </option>
                    ))}
                  </Select>
                  {selectedEvaluationForm && (
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      {availableFormTypes.find(f => f.value === selectedEvaluationForm)?.description || 
                       "Select the evaluation criteria for this scenario session"}
                    </Text>
                  )}
                  {!selectedEvaluationForm && (
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      Select the evaluation criteria for this scenario session
                    </Text>
                  )}
                </>
              )}
            </FormControl>

            {/* Section 5: Expiration Hours */}
            <FormControl>
              <FormLabel>Link Expires In</FormLabel>
              <Select
                value={expirationHours}
                onChange={(e) => setExpirationHours(e.target.value)}
                isDisabled={isSubmitting || !!generatedLink}
              >
                <option value="4">4 hours</option>
                <option value="8">8 hours</option>
                <option value="24">24 hours (1 day)</option>
                <option value="72">72 hours (3 days)</option>
                <option value="168">168 hours (1 week)</option>
                <option value="720">720 hours (30 days)</option>
              </Select>
              <Text fontSize="sm" color="gray.600" mt={1}>
                How long this link will remain active
              </Text>
            </FormControl>

            {/* Section 5: Email to Student Option */}
            <FormControl>
              <Checkbox
                isChecked={allowEmailToStudent}
                onChange={(e) => setAllowEmailToStudent(e.target.checked)}
                isDisabled={isSubmitting || !!generatedLink}
              >
                Allow evaluators to email evaluations to students
              </Checkbox>
              <Text fontSize="sm" color="gray.600" mt={1}>
                When enabled, evaluators will see a checkbox to send completed evaluations to students via email
              </Text>
            </FormControl>

            {/* Section 6: Notes/Drawing Area (only show when No Evaluation Form is selected) */}
            {selectedEvaluationForm === "No Evaluation Form" && (
              <FormControl>
                <FormLabel>Evaluation Notes</FormLabel>
                <Textarea
                  value={evaluationNotes}
                  onChange={(e) => setEvaluationNotes(e.target.value)}
                  placeholder="Enter evaluation notes here, or use a stylus to draw/write on a tablet..."
                  rows={8}
                  isDisabled={isSubmitting || !!generatedLink}
                  resize="vertical"
                />
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Use this area for free-form notes or drawing with a tablet and stylus
                </Text>
              </FormControl>
            )}

            {/* Section 7: Generated Link (conditional) */}
            {generatedLink && (
              <FormControl>
                <FormLabel>Generated Link</FormLabel>
                <InputGroup>
                  <Input 
                    value={generatedLink} 
                    isReadOnly 
                    cursor="pointer"
                    onClick={() => window.open(generatedLink, '_blank')}
                    title="Click to open in new tab"
                  />
                  <InputRightElement width="4.5rem">
                    <IconButton
                      icon={<FaCopy />}
                      size="sm"
                      onClick={handleCopyLink}
                      aria-label="Copy link"
                      mr={1}
                    />
                    <IconButton
                      icon={<FaPlay />}
                      size="sm"
                      onClick={() => window.open(generatedLink, '_blank')}
                      aria-label="Launch scenario"
                      colorScheme="green"
                    />
                  </InputRightElement>
                </InputGroup>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Use this link to access this scenario session
                </Text>
              </FormControl>
            )}

            {/* Success Message */}
            {successMessage && (
              <Text color="green.500" fontSize="sm">
                {successMessage}
              </Text>
            )}

            {/* Error Message */}
            {errorMessage && (
              <Text color="red.500" fontSize="sm">
                {errorMessage}
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} colorScheme="gray">
            Cancel
          </Button>
          {!generatedLink ? (
            <Button
              colorScheme="green"
              onClick={handleGenerateLink}
              isDisabled={!selectedCohort || !selectedEvaluationForm || isSubmitting || templatesFetching || availableFormTypes.length === 0}
              isLoading={isSubmitting}
            >
              {isSubmitting ? <Spinner size="sm" /> : "Generate Link"}
            </Button>
          ) : (
            <Button colorScheme="blue" onClick={handleClose}>
              Close
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RunScenarioModal;
